import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container } from '@components/atoms/Container';
import { Typography } from '@components/atoms/Typography';
import { Space } from '@components/atoms/Space';
import { Button, ButtonMode } from '@components/atoms/Button';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { Table } from '@components/atoms/Table';
import { useData } from '@hooks/useData';
import styles from './MyVehiclesData.module.css';
import { DeleteVehicleForm } from '../DeleteVehicleForm';

type Vehicle = {
    uuid?: string;
    brand?: string;
    model?: string;
    plate?: string;
};

export const MyVehiclesData: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, setUser } = useData();

    const vehicles: Vehicle[] = user?.data?.vehicles ?? [];

    const translations = {
        title: t('user-area.my-vehicles'),
        addVehicle: t('user-area.add-vehicle'),
        brand: t('car-info-step.brand'),
        model: t('car-info-step.model'),
        plate: t('car-info-step.plate'),
        seats: t('car-info-step.seats'),
        primary: t('user-area.primary-vehicle'),
        actions: t('common.actions'),
        edit: t('common.edit'),
        delete: t('common.delete'),
        noVehicles: t('user-area.no-vehicle'),
    };

    return (
        <Container classes={styles.containerBackground}>
            <div className="d-flex align-items-center position-relative">
                <div className="position-absolute start-0">
                    <RoundIcon
                        classes={styles.roundIcon}
                        icon="fa-thin fa-arrow-left"
                        variant={RoundIconVariants.variant1}
                        onClick={() => navigate(-1)}
                    />
                </div>

                <Typography fontSize="24" fontWeight="800" classes="m-auto">
                    {translations.title}
                </Typography>
            </div>

            <Space spaceBreakpoints={{ xs: { space: 50 } }} />

            {vehicles.length === 0 ? (
                <Typography color="muted">{translations.noVehicles}</Typography>
            ) : (
                <Table
                    compressedSize
                    headerCols={[
                        { classes: 'text-start', text: translations.brand },
                        { classes: 'text-start', text: translations.plate },
                        { classes: 'text-end', text: translations.actions, fit: true },
                    ]}
                    contentRows={vehicles.map((vehicle) => [
                        {
                            content: <Typography fontWeight="700">{`${vehicle.brand} ${vehicle.model}`}</Typography>,
                        },
                        {
                            content: <Typography>{vehicle.plate}</Typography>,
                        },
                        {
                            classes: 'text-end',
                            content: (
                                <div className="d-flex justify-content-end gap-2 position-relative">
                                    <RoundIcon
                                        icon="fa-light fa-pen-to-square"
                                        variant={RoundIconVariants.variant2}
                                        onClick={() =>
                                            navigate('/my-profile/vehicle/edit', {
                                                state: { vehicleUuid: vehicle.uuid },
                                            })
                                        }
                                    />

                                    {vehicle.uuid && (
                                        <DeleteVehicleForm
                                            vehicleUuid={vehicle.uuid}
                                            onDeleted={() => {
                                                if (!user?.data?.vehicles) return;

                                                setUser({
                                                    ...user,
                                                    data: {
                                                        ...user.data,
                                                        vehicles: user.data.vehicles.filter(
                                                            (v: Vehicle) => v.uuid !== vehicle.uuid
                                                        ),
                                                    },
                                                });
                                            }}
                                        />
                                    )}
                                </div>
                            ),
                        },
                    ])}
                />
            )}

            <Space spaceBreakpoints={{ xs: { space: 30 } }} />

            <div className="d-flex justify-content-end">
                <Button
                    label={translations.addVehicle}
                    mode={ButtonMode.primary}
                    onClick={() => navigate('/my-profile/vehicle/create')}
                />
            </div>
        </Container>
    );
};
